import React from "react";
import { type TooltipContentProps as DefaultTooltipContentProps, Tooltip as ReChartsTooltip } from "recharts";
import type { Payload } from "recharts/types/component/DefaultTooltipContent";
import { z } from "zod";

import { Truncated } from "~/components/truncated";
import { concat } from "~/tools/concat";
import { isEqualTime } from "~/tools/date-time";
import { formatAbsoluteTime } from "~/tools/date-time/format/format-absolute-time";
import { type FormatterUnit, formatUnits } from "~/tools/formatter-unit";
import { useScope } from "~/tools/impulse";
import { min } from "~/tools/min";
import { sortBy } from "~/tools/sort-by";
import { theme } from "~/tools/twin-migration";

import { useTimestampCursorState } from "../TimestampCursor";

import type { ChartDataPoint } from "./_create-chart-data-points";
import { LabelBullet } from "./_label-bullet";

interface TooltipProps extends Pick<ContentProps, "unit" | "thresholds" | "containerRef" | "viewBoxElement"> {
	points: ReadonlyArray<ChartDataPoint>;
}

export const Tooltip: React.FC<TooltipProps> = React.memo(({ points, ...props }) => {
	const scope = useScope();
	const timestampCursorState = useTimestampCursorState();

	const hasHighlightedMarkerInTimeSeries = React.useMemo(() => {
		const highlightedMarker = timestampCursorState.getHighlightedMarker(scope);

		return highlightedMarker == null || points.some((point) => isEqualTime(point.time, highlightedMarker));
	}, [scope, timestampCursorState, points]);

	if (!hasHighlightedMarkerInTimeSeries) {
		return null;
	}

	return (
		<ReChartsTooltip
			allowEscapeViewBox={props.viewBoxElement == null ? {} : { x: true, y: true }}
			// makes the tooltip to overlay the elements following after the chart
			content={({ payload, label, coordinate, offset, active }) => {
				if (!active) {
					return null;
				}

				return <Content {...props} coordinate={coordinate} label={label} offset={offset} payload={payload} />;
			}}
			cursor={{
				stroke: timestampCursorState.isHotkeyPressed(scope) ? theme("colors.purple.500") : theme("colors.graphite.500"),
				fill: theme("colors.blue.100"),
			}}
			isAnimationActive={false}
			wrapperStyle={{ zIndex: 500 }}
		/>
	);
});

if (import.meta.env.DEV) {
	Tooltip.displayName = "TimeSeriesChart.Tooltip";
}

const tooltipCoordinateInViewBox = ({
	viewBoxOffset,
	viewBoxSide,
	tooltipSide = 0,
	coordinate = 0,
	containerOffset = 0,
	offset,
}: {
	viewBoxOffset: undefined | number;
	viewBoxSide: undefined | number;
	tooltipSide: undefined | number;
	coordinate: undefined | number;
	containerOffset: undefined | number;
	offset: number;
}) => {
	if (viewBoxOffset == null || viewBoxSide == null) {
		return 0;
	}

	const containerPositionInViewBox = containerOffset - viewBoxOffset;
	const tooltipPositionInViewBox = containerPositionInViewBox + coordinate + offset;

	// if it needs to swap the tooltip direction, the offset should be applied twice to compensate it from the initial direction
	const reverseTooltipSide = tooltipSide + 2 * offset;
	const tooltipOverflow = tooltipPositionInViewBox + tooltipSide + offset - viewBoxSide;

	// reverse tooltip direction, when there is not enough space to fit the tooltip in the initial direction and there is a room to swap it
	if (tooltipOverflow > 0 && tooltipPositionInViewBox >= reverseTooltipSide) {
		return -reverseTooltipSide;
	}

	// if there is not enough space to reverse the direction, then just move the tooltip to the edge of the viewBox
	return min(0, -tooltipOverflow);
};

interface ContentProps extends Pick<DefaultTooltipContentProps<number, string>, "label" | "coordinate" | "offset"> {
	payload: null | ReadonlyArray<Payload<number, string>>;
	thresholds: undefined | ReadonlyArray<Omit<Payload<number, string>, "graphicalItemId">>;
	unit: FormatterUnit;
	containerRef: React.RefObject<SVGSVGElement>;
	viewBoxElement: null | HTMLElement;
}

const Content: React.FC<ContentProps> = ({
	label,
	payload,
	unit,
	coordinate,
	containerRef,
	offset = 0,
	viewBoxElement,
	thresholds,
}) => {
	const rootRef = React.useRef<HTMLDivElement>(null);

	const lines = React.useMemo(() => {
		const items = concat(payload ?? [], thresholds ?? []);

		return sortBy(items, [({ value }) => z.coerce.number().catch(Number.NEGATIVE_INFINITY).parse(value), "desc"]);
	}, [payload, thresholds]);

	const viewBoxRect = viewBoxElement?.getBoundingClientRect();
	const containerRect = viewBoxRect && containerRef.current?.getBoundingClientRect();
	const tooltipRect = viewBoxRect && rootRef.current?.getBoundingClientRect();

	const tooltipX = tooltipCoordinateInViewBox({
		viewBoxOffset: viewBoxRect?.left,
		viewBoxSide: viewBoxRect?.width,
		tooltipSide: tooltipRect?.width,
		coordinate: coordinate?.x,
		containerOffset: containerRect?.left,
		offset,
	});

	const tooltipY = tooltipCoordinateInViewBox({
		viewBoxOffset: viewBoxRect?.top,
		viewBoxSide: viewBoxRect?.height,
		tooltipSide: tooltipRect?.height,
		coordinate: coordinate?.y,
		containerOffset: containerRect?.top,
		offset,
	});

	React.useLayoutEffect(() => {
		// the recharts.Tooltip component has a tabIndex={-1} attribute that triggers blur for a focused input
		// which might be unexpected in cases when for instance a user looking thru autocomplete suggestions
		// but since recharts.Tooltip api does not provide any handle to define the wrapper tabIndex attribute
		// we should do it in that jQuery-like style
		rootRef.current?.parentElement?.removeAttribute("tabIndex");
	}, []);

	return (
		<div
			className="max-w-[330px] space-y-1 rounded-xs border border-gray-300 border-solid bg-white/90 px-3 py-4 text-xs/[14px] shadow-md"
			ref={rootRef}
			style={{
				transform: `translate3d(${tooltipX}px, ${tooltipY}px, 0)`,
			}}
		>
			{typeof label === "number" && (
				<div className="font-medium text-gray-800">{formatAbsoluteTime(label).toString()}</div>
			)}

			<ul className="m-0 list-none p-0">
				{lines.map((entry, index) => (
					<li className="flex flex-row items-center gap-2" key={entry.name ?? index}>
						{entry.stroke && !entry.color ? (
							<div className="h-px w-4 flex-none border-2 border-dashed" style={{ borderColor: entry.stroke }} />
						) : (
							<LabelBullet style={{ background: entry.color }} />
						)}

						<Truncated className="text-gray-700" tooltip={false}>
							{entry.name ?? ""}
						</Truncated>

						<span className="ml-auto whitespace-nowrap font-medium text-gray-800">
							{entry.value == null ? "-" : formatUnits(unit, entry.value)}
						</span>
					</li>
				))}
			</ul>
		</div>
	);
};
